import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DEFAULT_IMPRINT_HTML, DEFAULT_PRIVACY_HTML, type PlatformSettingsResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card } from '../components/ui';
import { RichTextEditor } from '../components/RichTextEditor';
import { PlatformTabs } from '../components/PlatformTabs';

/** Ein Abschnitt (Impressum oder Datenschutzerklärung) — eigener Editor,
 * eigener Speichern-Button, unabhängig vom jeweils anderen (wie die
 * einzelnen Karten auf Plattform › Design (Global)). `settingsKey` ist das
 * Feld in `platformSettings.legal`, das dieser Abschnitt schreibt. */
function LegalSection({
  title,
  liveUrl,
  value,
  defaultHtml,
  onSave,
}: {
  title: string;
  liveUrl: string;
  value: string;
  defaultHtml: string;
  onSave: (html: string) => Promise<unknown>;
}) {
  const [html, setHtml] = useState(value);
  useEffect(() => setHtml(value), [value]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => onSave(html),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const dirty = html !== value;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        <a
          href={liveUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-brand-accent hover:underline"
        >
          Live ansehen <ExternalLink size={13} />
        </a>
      </div>
      <RichTextEditor value={html} onChange={setHtml} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending || !dirty}>
          {save.isPending ? 'Speichert…' : 'Speichern'}
        </Button>
        <Button
          variant="ghost"
          type="button"
          onClick={() => {
            if (confirm(`„${title}" wirklich auf den Standardtext zurücksetzen? Erst mit „Speichern" endgültig.`)) {
              setHtml(defaultHtml);
            }
          }}
        >
          Auf Standard zurücksetzen
        </Button>
        {saved && <span className="text-sm text-green-600">Gespeichert ✓</span>}
        {dirty && !saved && <span className="text-sm text-slate-400">Ungespeicherte Änderungen</span>}
      </div>
      {error && (
        <div className="mt-3">
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** Plattform › Impressum & Datenschutz — freie Bearbeitung beider Seiten
 * durch den Plattformadmin, siehe docs/legal.md. Beide Texte liegen in
 * `platformSettings.legal` (JSONB, kein eigenes Datenbankschema nötig) und
 * werden öffentlich über `GET /public/legal` ausgeliefert
 * (`ImprintPage.tsx`/`PrivacyPage.tsx`, dort DOMPurify-sanitisiert
 * gerendert) — hier reicht deshalb der normale, admin-only
 * `/platform/settings`-Endpunkt zum Lesen/Schreiben. */
export function PlatformLegalPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api<PlatformSettingsResponse>('/platform/settings'),
  });

  const saveSection = (patch: { imprintHtml?: string; privacyHtml?: string }) =>
    api<PlatformSettingsResponse>('/platform/settings', { method: 'PATCH', body: { legal: patch } }).then((res) => {
      qc.setQueryData(['platform', 'settings'], res);
      return res;
    });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Impressum & Datenschutz</h1>
      <p className="text-sm text-slate-500">
        Beide Seiten sind ohne Login unter <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">/impressum</code>{' '}
        und <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">/datenschutz</code> erreichbar — verlinkt
        unten links in der Navigation und auf der Login-Seite.
      </p>

      {isLoading || !data ? (
        <p className="text-sm text-slate-400">Lädt…</p>
      ) : (
        <>
          <LegalSection
            title="Impressum"
            liveUrl="/impressum"
            value={data.legal.imprintHtml}
            defaultHtml={DEFAULT_IMPRINT_HTML}
            onSave={(html) => saveSection({ imprintHtml: html })}
          />
          <LegalSection
            title="Datenschutzerklärung"
            liveUrl="/datenschutz"
            value={data.legal.privacyHtml}
            defaultHtml={DEFAULT_PRIVACY_HTML}
            onSave={(html) => saveSection({ privacyHtml: html })}
          />
        </>
      )}
    </div>
  );
}
